/**
 * Planning the next session with the showrunner.
 *
 * The mid-session chat steers what is being prepared right now and dies with
 * the session. This is the other conversation — the one a GM has in their
 * head on the drive home — with something that has read every session.
 *
 * The Save button is what separates this from a chatbot: it distils what the
 * two of them agreed into a canon page the next session's agent reads on its
 * first tick. Until that is pressed, nothing here changes what gets prepared,
 * and the panel says so.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';

import type { PlanMessage } from '@/api/client';
import {
  getPlan,
  savePlan,
  sendPlanMessage,
  userFacingError,
} from '@/api/client';
import { BrandMark } from '@/components/BrandMark';
import { Markdown } from '@/components/Markdown';

/**
 * Who is talking: the product's own mark, the one in the browser tab.
 *
 * A chat with no face on the other side read as a form with a text box, and
 * a generic glyph would only have said "a bot". This is the same circular
 * mark as the masthead, drawn inline so it stays crisp at 32px and appears
 * beside the first reply without a network round trip.
 */
function Avatar() {
  return (
    <span className="plan__avatar" aria-hidden="true">
      <BrandMark />
    </span>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none"
      stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 8.5 6.5 12 13 4.5" />
    </svg>
  );
}

export function PlanChat({ campaignId }: { campaignId: string }) {
  const [messages, setMessages] = useState<PlanMessage[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  // The first reply really is the campaign being read — premise, canon, every
  // recap, the last session verbatim — and it is the slow one. Every reply
  // after that continues a conversation the model is already holding, so
  // saying "reading the campaign" again would be describing work that is not
  // happening.
  const [waiting, setWaiting] = useState<'reading' | 'thinking'>('reading');
  const [saving, setSaving] = useState(false);
  // "Saved" has to outlast the request by long enough to be read. The badge
  // in the header is easy to miss, and a button that flicks back to its
  // resting label leaves a GM unsure whether the press registered at all.
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const result = await getPlan(campaignId);
      setMessages(result.messages);
      setSaved(result.saved_plan);
    } catch {
      /* a campaign with no planning history is the normal case */
    }
  }, [campaignId]);

  useEffect(() => {
    setMessages([]);
    setSaved(null);
    setOpen(false);
    void load();
  }, [load]);

  useEffect(() => {
    const node = scroller.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const content = value.trim();
    if (!content || busy) return;
    setBusy(true);
    setError(null);
    setWaiting(messages.length === 0 ? 'reading' : 'thinking');
    // Optimistic, so the GM's own line lands instantly; the reply takes a
    // few seconds because it is reading the whole campaign to answer.
    setMessages((m) => [
      ...m,
      { id: `local-${Date.now()}`, role: 'user', content },
    ]);
    setValue('');
    try {
      const reply = await sendPlanMessage(campaignId, content);
      setMessages((m) => [...m, reply]);
    } catch (err) {
      setError(
        userFacingError(
          err,
          'Throughline couldn’t answer just now. Your message is safe, so please try again.',
        ),
      );
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setJustSaved(false);
    try {
      const result = await savePlan(campaignId);
      setSaved(result.saved_plan);
      setJustSaved(true);
      // Open the summary as well as announcing it: the most useful proof
      // that a save worked is being able to read what was saved.
      setShowSaved(true);
      window.setTimeout(() => setJustSaved(false), 6_000);
    } catch (err) {
      setError(
        userFacingError(
          err,
          'The plan couldn’t be saved just now. Please try again.',
        ),
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card">
      <header className="card__head">
        <h2 className="card__title">Plan the next session</h2>
        {saved ? <span className="badge badge--ok">plan saved</span> : null}
      </header>

      <p className="card__body card__lede">
        Talk through what comes next. Throughline has read every session of
        this campaign, and when you settle on a plan, saving it means the next
        session starts from it.
      </p>

      {!open && messages.length === 0 ? (
        <button
          type="button"
          className="button"
          onClick={() => setOpen(true)}
        >
          Start planning
        </button>
      ) : (
        <>
          <div className="plan__scroll" ref={scroller}>
            {messages.length === 0 ? (
              <div className="plan__intro">
                <Avatar />
                <div>
                  <span className="plan__who">Throughline</span>
                  <p className="plan__body plan__body--plain">
                    I have read every session of this campaign. Ask me what
                    should open next time, which thread is overdue, or whether
                    an idea of yours actually fits what we have established.
                  </p>
                </div>
              </div>
            ) : (
              messages.map((m) => (
                <div key={m.id} className={`plan__msg plan__msg--${m.role}`}>
                  {m.role === 'assistant' ? <Avatar /> : null}
                  <div className="plan__bubble">
                    <span className="plan__who">
                      {m.role === 'user' ? 'You' : 'Throughline'}
                    </span>
                    {/* The showrunner writes Markdown — headings, bold, lists
                        — and this rendered it as literal asterisks and hashes,
                        the same bug the session recap had. A GM's own message
                        stays plain text: they are typing, not authoring. */}
                    {m.role === 'assistant' ? (
                      <div className="plan__body">
                        <Markdown md={m.content} />
                      </div>
                    ) : (
                      <p className="plan__body plan__body--plain">{m.content}</p>
                    )}
                  </div>
                </div>
              ))
            )}
            {busy ? (
              <div className="plan__msg plan__msg--assistant">
                <Avatar />
                <p className="plan__thinking">
                  <span className="spinner" aria-hidden="true" />
                  {waiting === 'reading'
                    ? 'Reading your whole campaign, every session and every thread. The first reply takes a moment.'
                    : 'Thinking…'}
                </p>
              </div>
            ) : null}
          </div>

          {error ? (
            <div className="notice notice--error" role="alert">
              {error}
            </div>
          ) : null}

          <form className="plan__form" onSubmit={submit}>
            <textarea
              className="field__input field__input--multiline"
              rows={2}
              value={value}
              placeholder="I want to open on the wagon leaving. Does that work with where we left it?"
              disabled={busy}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void submit(e as unknown as FormEvent);
                }
              }}
            />
            <div className="plan__actions">
              <button
                type="submit"
                className="button button--primary"
                disabled={busy || !value.trim()}
              >
                Send
              </button>
              <button
                type="button"
                className={justSaved ? 'button button--saved' : 'button'}
                onClick={() => void handleSave()}
                disabled={saving || messages.length === 0}
                aria-live="polite"
              >
                {saving ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Saving the plan…
                  </>
                ) : justSaved ? (
                  <>
                    <CheckIcon />
                    Saved for next session
                  </>
                ) : (
                  'Save as the plan'
                )}
              </button>
            </div>
            <span className="field__hint">
              Nothing here reaches the next session until you save it.
            </span>
          </form>
        </>
      )}

      {justSaved ? (
        <div className="notice notice--ok" role="status">
          <CheckIcon />
          <span>Saved. Your next session starts with this plan.</span>
        </div>
      ) : null}

      {saved ? (
        <details
          className="plan__saved"
          open={showSaved}
          onToggle={(e) => setShowSaved((e.target as HTMLDetailsElement).open)}
        >
          <summary>What the next session will know</summary>
          <Markdown md={saved} />
        </details>
      ) : null}
    </section>
  );
}
